/** Handle over a live file input. Call `destroy()` when the owner is torn down. */
export interface FilePicker {
  /** Opens the native picker. Resolves with the chosen file, or null if cancelled. */
  open(): Promise<File | null>;
  destroy(): void;
}

export interface FilePickerOptions {
  /** `accept` attribute, e.g. `'image/*,.pdf'`. */
  accept?: string;
  /** Ask mobile browsers to go straight to the camera. */
  capture?: 'environment' | 'user';
}

/**
 * Creates a reusable file input, attached to the document and owned by the
 * caller.
 *
 * The obvious alternative — building a detached `<input type="file">` on every
 * click and dropping it afterwards — breaks on iOS/iPadOS. The camera app takes
 * over the whole screen, and while it is in the foreground WebKit is free to
 * collect an input that nothing in the document references. When that happens
 * the `change` event never fires on return, so "Usar foto" behaves exactly like
 * "Cancelar" and the capture is silently lost. An element that lives in the DOM
 * for the lifetime of the component is not eligible for that collection.
 */
export function createFilePicker(options: FilePickerOptions = {}): FilePicker {
  const input = document.createElement('input');
  input.type = 'file';
  if (options.accept) input.accept = options.accept;
  if (options.capture) input.setAttribute('capture', options.capture);

  // Off-screen rather than `display: none` / `hidden`: Safari has historically
  // refused to open the picker for an input that isn't rendered.
  input.style.cssText =
    'position:fixed;top:0;left:0;width:1px;height:1px;opacity:0;pointer-events:none;';
  input.tabIndex = -1;
  input.setAttribute('aria-hidden', 'true');
  document.body.appendChild(input);

  let pending: ((file: File | null) => void) | null = null;

  const settle = (file: File | null): void => {
    const resolve = pending;
    pending = null;
    resolve?.(file);
  };

  input.addEventListener('change', () => settle(input.files?.[0] ?? null));
  // Not universally supported; when it is, a dismissed picker settles right away
  // instead of leaving the promise hanging until the next open().
  input.addEventListener('cancel', () => settle(null));

  return {
    open(): Promise<File | null> {
      settle(null); // A picker still open from a previous call counts as cancelled.
      return new Promise<File | null>((resolve) => {
        pending = resolve;
        // Picking the same file twice in a row must still fire `change`.
        input.value = '';
        // Synchronous, so the click stays inside the user gesture.
        input.click();
      });
    },
    destroy(): void {
      settle(null);
      input.remove();
    },
  };
}
